/**
 * react-query hooks for the Permissions admin UI. Cache keys are
 * standardised so all mutations can target invalidations from one place.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cgToast } from 'modules/Common/ui/toast';
import * as api from '../api/permissionsApi';
import i18n from 'i18n';
import { apiErrorMessage } from 'helpers/serverIssues';

export const PERMISSIONS_KEYS = {
  roles: ['permissions', 'roles'],
  roleGrants: (slug) => ['permissions', 'roles', slug, 'grants'],
  // PermissionsProvider's /me payload. Any mutation that could change a
  // user's effective permission set (grants edit, role create/delete with
  // existing assignments, user-role assignment) must invalidate this so the
  // gating UI reflects the change without waiting for the 60s staleTime.
  me: ['permissions', 'me'],
};

// ----- queries -----

export const useRoles = () =>
  useQuery({
    queryKey: PERMISSIONS_KEYS.roles,
    queryFn: api.listRoles,
    staleTime: 30_000,
    // Org-profile panels edit inline; a focus-triggered refetch remounts rows
    // and was wiping in-flight dropdown / matrix state.
    refetchOnWindowFocus: false,
  });

export const useRoleGrants = (slug) =>
  useQuery({
    queryKey: PERMISSIONS_KEYS.roleGrants(slug),
    queryFn: () => api.getRoleGrants(slug),
    enabled: Boolean(slug),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

// ----- mutations -----

const notifyError = (action) => (err) => {
  cgToast.error(i18n.t('admin:errors.failedTo', 'Failed to {{action}}', { action }), {
    description: apiErrorMessage(err) || i18n.t('admin:errors.unexpected', 'Unexpected error'),
  });
};

// Conservative blanket invalidation: any role/grant/assignment mutation may
// change the caller's own effective permissions (e.g. they edited a role
// they hold). We don't track which user IDs touch which roles client-side,
// so we drop the whole /me cache and let the next render refetch.
const invalidateMe = (qc) => qc.invalidateQueries({ queryKey: PERMISSIONS_KEYS.me });

export const useUpdateRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, patch }) => api.updateRole(slug, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PERMISSIONS_KEYS.roles });
      invalidateMe(qc);
      cgToast.success(i18n.t('admin:toasts.roleUpdated', 'Role updated'), { description: '' });
    },
    onError: notifyError(i18n.t('admin:actions.updateRoleVerb', 'update role')),
  });
};

export const useUpdateRoleGrants = (slug) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (permissions) => api.updateRoleGrants(slug, permissions),
    // Optimistic update: write the new permissions into the cache so the
    // editor reflects the save immediately. On error react-query rolls back.
    onMutate: async (permissions) => {
      await qc.cancelQueries({ queryKey: PERMISSIONS_KEYS.roleGrants(slug) });
      const previous = qc.getQueryData(PERMISSIONS_KEYS.roleGrants(slug));
      qc.setQueryData(PERMISSIONS_KEYS.roleGrants(slug), { permissions });
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(PERMISSIONS_KEYS.roleGrants(slug), ctx.previous);
      }
      notifyError(i18n.t('admin:actions.savePermissionsVerb', 'save role permissions'))(err);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PERMISSIONS_KEYS.roles });
      invalidateMe(qc);
      cgToast.success(i18n.t('admin:toasts.permissionsSaved', 'Permissions saved'), {
        description: '',
      });
    },
  });
};
